import { type MediaFieldsFragment } from "@/gql/graphql";
import { ImoovaImage } from "@/app/Media/Components/Image";
import { PaperClipIcon } from "@heroicons/react/24/outline";
import { Spinner } from "@/lib/Components/Layout/Loaders/Spinner";
import { cn } from "@/lib/utils";
import { useState } from "react";

type Props = {
  attachment: MediaFieldsFragment;
  isMine: boolean;
};

function isImage(mimeType: string) {
  return mimeType.startsWith("image/");
}

export function AttachmentPreview({ attachment, isMine }: Props) {
  if (isImage(attachment.mime_type)) {
    return <ImageAttachment attachment={attachment} />;
  }

  return <FileAttachment attachment={attachment} isMine={isMine} />;
}

function ImageAttachment({ attachment }: { attachment: MediaFieldsFragment }) {
  const [isLoaded, setIsLoaded] = useState(false);

  return (
    <a
      href={attachment.url}
      target="_blank"
      rel="noopener noreferrer"
      className="relative block h-48 w-64 overflow-hidden rounded"
    >
      {!isLoaded ? (
        <div className="absolute inset-0 flex items-center justify-center">
          <Spinner className="size-6 text-gray-400" />
        </div>
      ) : null}
      <ImoovaImage
        src={attachment.url}
        alt={attachment.name}
        className={cn(
          "h-full w-full object-cover transition-opacity duration-200",
          isLoaded ? "opacity-100" : "opacity-0",
        )}
        onLoad={() => setIsLoaded(true)}
      />
    </a>
  );
}

function FileAttachment({
  attachment,
  isMine,
}: {
  attachment: MediaFieldsFragment;
  isMine: boolean;
}) {
  return (
    <a
      href={attachment.url}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(
        "flex items-center gap-2 rounded px-2 py-1 text-xs transition-colors",
        isMine
          ? "text-gray-700 hover:bg-gray-200"
          : "text-blue-100 hover:bg-blue-500",
      )}
    >
      <PaperClipIcon className="h-3.5 w-3.5 shrink-0" />
      <span className="truncate">{attachment.name}</span>
      <span className="shrink-0 opacity-60">
        {formatFileSize(attachment.size)}
      </span>
    </a>
  );
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${Math.round(bytes / 1024)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
